import { useState, useEffect, useMemo, useRef } from 'react';
import {
  verifySpendAndOppsRow,
  verifyRowData,
  isStoreValid,
  formatStoreOptions,
  stableObject,
} from '../../../constants/utils';
import {
  ACTUAL_OPPS_KEY,
  ACTUAL_SPEND_KEY,
  MONTHLY,
  MONTHLY_OPPS_SEASONALITY,
  MONTHLY_SPENDS_SEASONALITY,
  OPPS_DATA,
  PLANNED_OPPS_KEY,
  PLANNED_SPEND_KEY,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_SEASONALITY,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_SEASONALITY_LOCAL,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_SEASONALITY,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_SEASONALITY_LOCAL,
  SPENDS_DATA,
  WEEKLY,
  WEEKLY_OPPS_SEASONALITY,
  WEEKLY_SPENDS_SEASONALITY,
} from '../constants';
import {
  transformHeaders,
  transformWeeklyData,
  transformMonthlyData,
  getSpendsAndOppsData,
  filterData,
} from '../service/SpendAndOppurtunitiesService';

export const useSpendAndOppsHook = (
  selectedStore,
  viewType,
  currentYear,
  userStores,
  isDownloading
) => {
  const [oppsData, setOppsData] = useState([]);
  const [spendsData, setSpendsData] = useState([]);
  const [oppsheaders, setOppsHeaders] = useState([]);
  const [spendsheaders, setSpendsHeaders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadReportData, setDownloadReportData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [spendToggleState, setSpendToggleState] = useState(false);
  const [showSpendToggle, setShowSpendToggle] = useState(false);
  const storeIds = userStores.map((store) => store.branch_number);

  // Single flag to track if data has been fetched
  const hasDataBeenFetched = useRef(false);

  const allStores = useMemo(() => formatStoreOptions(userStores), [stableObject(userStores)]);

  const getPayload = (key, selectedStore) => {
    const payload = {
      [key]: {
        filters: [
          {
            col: 'store_id',
            op: 'in',
            val: selectedStore,
          },
        ],
      },
    };
    return payload;
  };

  const setUpStoreData = (data) => {
    if (viewType === WEEKLY) {
      const transformedOppsData = transformWeeklyData(
        data,
        OPPS_DATA,
        selectedStore,
        currentYear,
        false
      );
      const transformedSpendsData = transformWeeklyData(
        data,
        SPENDS_DATA,
        selectedStore,
        currentYear,
        false
      );
      const transformedHeaders = transformHeaders(data, selectedStore);
      const transformedOppsDataArray = Object.values(transformedOppsData);
      const transformedSpendsDataArray = Object.values(transformedSpendsData);
      const verifiedOppsData = transformedOppsDataArray.map((row) =>
        verifySpendAndOppsRow(row, transformedHeaders)
      );
      const verifiedSpendsData = transformedSpendsDataArray.map((row) =>
        verifySpendAndOppsRow(row, transformedHeaders)
      );
      setOppsHeaders(transformedHeaders);
      setSpendsHeaders(transformedHeaders);
      setOppsData(verifiedOppsData);
      setSpendsData(verifiedSpendsData);
    } else if (viewType === MONTHLY) {
      const { rowData: rowDataOpps, tableHeaders: tableOppsHeaders } = transformMonthlyData(
        data,
        OPPS_DATA,
        selectedStore,
        currentYear,
        false
      );
      const rowOppsData = rowDataOpps;
      const { rowData: rowDataSpends, tableHeaders: tableSpendsHeaders } = transformMonthlyData(
        data,
        SPENDS_DATA,
        selectedStore,
        currentYear,
        false
      );
      const rowSpendsData = rowDataSpends;
      const newRowOppsData = verifyRowData(rowOppsData, tableOppsHeaders);
      const newRowSpendsData = verifyRowData(rowSpendsData, tableSpendsHeaders);
      setOppsHeaders(tableOppsHeaders);
      setSpendsHeaders(tableSpendsHeaders);
      setOppsData(newRowOppsData);
      setSpendsData(newRowSpendsData);
    }
  };

  const setUpDownloadReportData = (data) => {
    const downloadData = allStores
      .map((store) => store.value)
      ?.map((selectedStore) => {
        const header = { Header: 'Monthly Seasonality', accessor: MONTHLY_OPPS_SEASONALITY };
        let storeTemp = { store_id: selectedStore, rows: [], columns: [header] };
        if (viewType === MONTHLY) {
          const { rowData: rowDataOpps, tableHeaders: tableOppsHeaders } = transformMonthlyData(
            data,
            OPPS_DATA,
            selectedStore,
            currentYear,
            true
          );
          const rowOppsData =
            filterData({
              rowData: rowDataOpps,
              currentYear: currentYear,
              actualKey: ACTUAL_OPPS_KEY,
              plannedKey: PLANNED_OPPS_KEY,
              dataKey: MONTHLY_OPPS_SEASONALITY,
            }) || [];
          const { rowData: rowDataSpends, tableHeaders: tableSpendsHeaders } = transformMonthlyData(
            data,
            SPENDS_DATA,
            selectedStore,
            currentYear,
            true
          );
          const rowSpendsData =
            filterData({
              rowData: rowDataSpends,
              currentYear: currentYear,
              actualKey: ACTUAL_SPEND_KEY,
              plannedKey: PLANNED_SPEND_KEY,
              dataKey: MONTHLY_SPENDS_SEASONALITY,
            }) || [];
          const newRowOppsData = verifyRowData(rowOppsData, tableOppsHeaders);
          const newRowSpendsData = verifyRowData(rowSpendsData, tableSpendsHeaders);
          if (newRowOppsData) {
            storeTemp = {
              ...storeTemp,
              columns: [
                header,
                ...tableOppsHeaders.filter((col) => col.accessor !== MONTHLY_OPPS_SEASONALITY),
              ],
              rows: newRowOppsData,
            };
          }
          if (newRowSpendsData) {
            const propertySpends = MONTHLY_SPENDS_SEASONALITY;
            const propertyOpps = MONTHLY_OPPS_SEASONALITY;
            const rowsData = newRowSpendsData.map((row) =>
              row[propertySpends] ? { ...row, [propertyOpps]: row.Monthly_Spends_Seasonality } : row
            );
            storeTemp = {
              ...storeTemp,
              rows: [...(storeTemp.rows || []), ...rowsData],
            };
          }
        } else if (viewType === WEEKLY) {
          const transformedOppsData = transformWeeklyData(
            data,
            OPPS_DATA,
            selectedStore,
            currentYear,
            true
          );
          const transformedSpendsData = transformWeeklyData(
            data,
            SPENDS_DATA,
            selectedStore,
            currentYear,
            true
          );
          const transformedHeaders = transformHeaders(data, selectedStore);
          const transformedOppsDataArray =
            filterData({
              rowData: Object.values(transformedOppsData),
              currentYear: currentYear,
              actualKey: ACTUAL_OPPS_KEY,
              plannedKey: PLANNED_OPPS_KEY,
              dataKey: WEEKLY_OPPS_SEASONALITY,
            }) || [];
          const transformedSpendsDataArray =
            filterData({
              rowData: Object.values(transformedSpendsData),
              currentYear: currentYear,
              actualKey: ACTUAL_SPEND_KEY,
              plannedKey: PLANNED_SPEND_KEY,
              dataKey: WEEKLY_SPENDS_SEASONALITY,
            }) || [];
          const verifiedOppsData = transformedOppsDataArray.map((row) =>
            verifySpendAndOppsRow(row, transformedHeaders)
          );
          const verifiedSpendsData = transformedSpendsDataArray.map((row) =>
            verifySpendAndOppsRow(row, transformedHeaders)
          );
          if (verifiedOppsData) {
            const header = { Header: 'Weekly Seasonality', accessor: WEEKLY_OPPS_SEASONALITY };
            storeTemp = {
              ...storeTemp,
              rows: verifiedOppsData,
              columns: [header, ...transformedHeaders],
            };
          }
          if (verifiedSpendsData) {
            const propertySpends = WEEKLY_SPENDS_SEASONALITY;
            const propertyOpps = WEEKLY_OPPS_SEASONALITY;
            const rowsData = verifiedSpendsData.map((row) =>
              row[propertySpends] ? { ...row, [propertyOpps]: row.Weekly_Spends_Seasonality } : row
            );

            storeTemp = { ...storeTemp, rows: [...storeTemp.rows, ...rowsData] };
          }
        }
        return storeTemp;
      });

    setDownloadReportData(downloadData);
  };

  const fetchData = async () => {
    // If already loading or data has been fetched during this session, don't fetch again
    if (isLoading) return;

    try {
      setIsLoading(true);

      const monthlyKey = !spendToggleState
        ? SPENDS_AND_OPPORTUNITIES__MONTHLY_SEASONALITY
        : SPENDS_AND_OPPORTUNITIES__MONTHLY_SEASONALITY_LOCAL;

      const weeklyKey = !spendToggleState
        ? SPENDS_AND_OPPORTUNITIES__WEEKLY_SEASONALITY
        : SPENDS_AND_OPPORTUNITIES__WEEKLY_SEASONALITY_LOCAL;

      const key = viewType === MONTHLY ? monthlyKey : weeklyKey;
      const payload = getPayload(key, storeIds);

      const result = await getSpendsAndOppsData(payload, key);

      if (result?.data?.[key]) {
        setRowData(result.data[key]);
        setUpStoreData(result.data[key]);
        setUpDownloadReportData(result.data[key]);
        hasDataBeenFetched.current = true;
      } else {
        console.error('Invalid API response structure:', result);
        setError(new Error('Invalid API response structure'));
      }
    } catch (error) {
      console.error('Error fetching spend and opportunities data:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Single useEffect to handle initial data loading and parameter changes
  useEffect(() => {
    const shouldFetchData = () => {
      // Check if all required parameters are present
      const hasRequiredParams = 
        selectedStore !== undefined &&
        viewType !== undefined &&
        currentYear !== undefined &&
        storeIds.length > 0;

      // Either this is the first data fetch, or parameters have changed
      return hasRequiredParams && (hasDataBeenFetched.current || true);
    };

    if (shouldFetchData()) {
      // Reset data states before fetching
      setOppsData([]);
      setSpendsData([]);
      setError(null);

      // Fetch data with a slight delay to prevent multiple simultaneous calls
      const timeoutId = setTimeout(() => {
        fetchData();
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedStore, viewType, currentYear, spendToggleState]);

  // Handle toggle visibility whenever store changes
  useEffect(() => {
    setShowSpendToggle(isStoreValid(selectedStore, allStores));
  }, [selectedStore, allStores]);

  return {
    oppsData,
    spendsData,
    oppsheaders,
    spendsheaders,
    isLoading,
    error,
    downloadReportData,
    spendToggleState,
    setSpendToggleState,
    showSpendToggle,
    allStores,
  };
};
